import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Users,
  Trophy,
  ExternalLink,
  Clock,
  TrendingUp,
  Ban,
  Loader2,
  ChevronRight,
  Target,
  AlertTriangle,
} from 'lucide-react';

const CreatorTeamPagesSection = ({ campaignId }) => {
  const { token } = useAuth();
  const [teamPages, setTeamPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchTeamPages();
  }, [campaignId]);

  const fetchTeamPages = async () => {
    if (!campaignId) return;
    
    try {
      const [pagesRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/team-pages/campaign/${campaignId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/campaigns/${campaignId}/team-stats`)
      ]);
      
      if (pagesRes.ok) {
        const data = await pagesRes.json();
        setTeamPages(data.team_pages || []);
      }
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (err) {
      setError('Failed to load team pages');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (teamPageId) => {
    setActionLoading(teamPageId);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/team-pages/${teamPageId}/disable`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Disabled by campaign creator' })
      });
      
      if (response.ok) {
        fetchTeamPages();
      } else if (response.status === 429) {
        setError('Please wait a moment before trying again.');
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Failed to disable team page');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="creator-team-pages-section">
      {/* Stats Header */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-card rounded-xl p-4 border border-border text-center">
            <p className="text-2xl font-bold text-foreground">{stats.active_team_pages || 0}</p>
            <p className="text-sm text-muted-foreground">Active Fundraisers</p>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border text-center">
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(stats.team_raised_total)}</p>
            <p className="text-sm text-muted-foreground">Team Raised</p>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border text-center">
            <p className="text-2xl font-bold text-foreground">{stats.team_pages_count || 0}</p>
            <p className="text-sm text-muted-foreground">Total Created</p>
          </div>
        </div>
      )}

      {/* No Team Pages */}
      {teamPages.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Team Fundraisers Yet</h3>
          <p className="text-muted-foreground mb-4">
            Supporters haven't started fundraising for your campaign yet.
          </p>
          <p className="text-sm text-muted-foreground">
            Make sure team fundraising is enabled in your campaign settings.
          </p>
        </div>
      ) : (
        <>
          {/* Leaderboard Link */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-foreground">Team Fundraisers</h3>
            <Link to={`/campaign/${campaignId}/leaderboard`} className="text-sm text-emerald-500 hover:text-emerald-400 flex items-center gap-1">
              View Leaderboard <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Team Pages List */}
          <div className="space-y-4">
            {teamPages.map((page) => (
              <div
                key={page.id}
                className={`bg-card rounded-xl p-4 border transition-colors ${
                  page.status === 'active' ? 'border-border' : 'border-red-500/30 bg-red-500/5'
                }`}
              >
                <div className="flex items-start gap-4">
                  <img
                    src={page.owner_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(page.owner_name)}`}
                    alt={page.owner_name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Link to={`/fundraise/${page.slug}`} className="font-semibold text-foreground hover:text-emerald-400 truncate">
                        {page.title}
                      </Link>
                      {page.status !== 'active' && (
                        <Badge variant="destructive" className="text-xs">
                          <Ban className="w-3 h-3 mr-1" /> {page.status}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      by {page.owner_name} • Created {formatDate(page.created_at)}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-emerald-400 font-medium">
                        {formatCurrency(page.amount_raised)} raised
                      </span>
                      <span className="text-muted-foreground">
                        {page.donor_count || 0} donors
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/fundraise/${page.slug}`} target="_blank">
                      <Button variant="ghost" size="icon">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </Link>
                    {page.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:bg-red-500/10"
                        onClick={() => handleDisable(page.id)}
                        disabled={actionLoading === page.id}
                      >
                        {actionLoading === page.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Ban className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Info Box */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mt-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-foreground mb-1">Managing Team Fundraisers</p>
            <p className="text-muted-foreground">
              You can disable any team page that violates your campaign's terms. 
              Disabled pages won't accept new donations but existing donation records are preserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorTeamPagesSection;
